/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    ArrayExtensions,
    convertDimensionTypeToProductDimensionType,
    createInventoryAvailabilitySearchCriteria,
    Dictionary,
    DimensionTypes,
    generateImageUrl,
    getProductPageUrlSync,
    IDimensionsApp,
    StringExtensions,
    validateCatalogId
} from '@msdyn365-commerce-modules/retail-actions';

import {
    getEstimatedAvailabilityAsync,
    getAttributeValuesAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import {
    format,
    getPayloadObject,
    getTelemetryAttributes,
    IncrementalQuantity,
    ITelemetryContent,
    onTelemetryClick
} from '@msdyn365-commerce-modules/utilities';
import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import {
    IPriceComponentResources,
    ISwatchItem,
    PriceComponent,
    ProductComponentSwatchComponent,
    RatingComponent
} from '@msdyn365-commerce/components';
import {
    getCatalogId,
    IAny,
    IComponent,
    IComponentProps,
    ICoreContext,
    IGeneric,
    IGridSettings,
    IImageData,
    IImageSettings,
    Image,
    msdyn365Commerce
} from '@msdyn365-commerce/core';
import { getCartState } from '@msdyn365-commerce/global-state';
import {
    AttributeSwatch,
    AttributeValue,
    CartLine,
    PriceLookupContext,
    ProductDimension,
    ProductPrice,
    ProductSearchResult,
    ProductType,
    ProjectionDomain,
    SimpleProduct,
    SearchArea,
    ProductWarehouseInventoryAvailability
} from '@msdyn365-commerce/retail-proxy';
import { addCartLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import React, { useState } from 'react';
import { getActivePricesAction, GetActivePricesInput } from '../../../../actions/getactiveprices.action';
import { GetProductUnitOfMeasureInput, getProductUnitsOfMeasureAction } from '../../../../actions/getproductunitsofmeasure.action';
import { ISearchResultContainerResources } from '../../definition-extensions/search-result-container.ext.props.autogenerated';
import MsDyn365 from '@msdyn365-commerce/core';

export enum BuyBoxConstants {
    zero = 0
}

export interface IProductComponentProps extends IComponentProps<{ product?: ProductSearchResult; productPrice?: ProductPrice }> {
    className?: string;
    imageSettings?: IImageSettings;
    savingsText?: string;
    freePriceText?: string;
    originalPriceText?: string;
    currentPriceText?: string;
    ratingAriaLabel?: string;
    ratingCountAriaLabel?: string;
    allowBack?: boolean;
    telemetryContent?: ITelemetryContent;
    quickViewButton?: React.ReactNode;
    productComparisonButton?: React.ReactNode;
    isEnabledProductDescription?: boolean;
    isPriceMinMaxEnabled?: boolean;
    priceResources?: IPriceComponentResources;
    inventoryLabel?: string;
    dimensionAvailabilities?: IProductsDimensionsAvailabilities[];
    swatchItemAriaLabel?: string;
    resources?: ISearchResultContainerResources;
    isStoreSelected?: boolean | undefined;
    storeId?: string;
}

export interface IStoreDataCustom {
    Latitude?: number;
    Longitude?: number;
    StoreId?: string;
    StoreName?: string;
}

export interface IProductComponent extends IComponent<IProductComponentProps> {}

const PriceComponentActions = {};

/**
 * Gets the product page url from the default swatch selected.
 * @param  productData - Product card to be rendered.
 * @returns The default color swatch selected if any.
 */
function getDefaultSwatchSelected(coreContext: ICoreContext, productData?: ProductSearchResult): AttributeSwatch | null {
    if (!productData || !productData.AttributeValues) {
        return null;
    }

    const siteContext = coreContext as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;

    const attributeSwatches = productData.AttributeValues.find(
        attributeValue => attributeValue.KeyName?.toLocaleLowerCase() === dimensionToPreSelectInProductCard
    )?.Swatches;

    if (!ArrayExtensions.hasElements(attributeSwatches)) {
        return null;
    }

    const defaultSwatch = attributeSwatches.find(item => item.IsDefault === true) ?? attributeSwatches[0];
    return defaultSwatch;
}

/**
 * Gets the product image from the default swatch selected.
 * @param  coreContext - Context of the module using the component.
 * @param  productData - Product card to be rendered.
 * @returns The product card image url.
 */
function getProductImageUrlFromDefaultSwatch(coreContext: ICoreContext, productData?: ProductSearchResult): string | undefined {
    const defaultSwatch = getDefaultSwatchSelected(coreContext, productData);
    const swatchProductImageUrls = defaultSwatch?.ProductImageUrls;
    if (!ArrayExtensions.hasElements(swatchProductImageUrls)) {
        return productData?.PrimaryImageUrl;
    }

    return generateImageUrl(swatchProductImageUrls[0], coreContext.request.apiSettings);
}

/**
 * Updates the product url link to product details page.
 * @param  productDetailsPageUrl - Product page url.
 * @param  coreContext - Context of the module using the component.
 * @param  queryString - Querystring to be added to the URL.
 * @returns The update product page url.
 */
function updateProductUrl(productDetailsPageUrl: string, coreContext: ICoreContext, queryString: string): string {
    const sourceUrl = new URL(productDetailsPageUrl, coreContext.request.apiSettings.baseUrl);
    if (sourceUrl.search) {
        sourceUrl.search += `&${queryString}`;
    } else {
        sourceUrl.search += queryString;
    }

    const updatedUrl = new URL(sourceUrl.href);
    return updatedUrl.pathname + sourceUrl.search;
}

/**
 * Gets the react node for product unit of measure display.
 * @param  unitOfMeasure - DefaultUnitOfMeasure property from product.
 * @returns The node representing markup for unit of measure component.
 */
function renderProductUnitOfMeasure(unitOfMeasure?: string): JSX.Element | null {
    if (!unitOfMeasure) {
        return null;
    }
    return (
        <div className='msc-product__unit-of-measure'>
            <span>{unitOfMeasure}</span>
        </div>
    );
}

/**
 * Gets the react node for product availability.
 * @param inventoryAvailabilityLabel - The product information.
 * @returns The node representing markup for product availability.
 */
function renderProductAvailability(inventoryAvailabilityLabel: string | undefined): JSX.Element | null {
    if (!inventoryAvailabilityLabel || inventoryAvailabilityLabel === '') {
        return null;
    }

    return (
        <div className='msc-product__availability'>
            <span>{inventoryAvailabilityLabel}</span>
        </div>
    );
}

/**
 * Renders product comparison button similar to the quick view button.
 * @param productComparisonButton - React element of the button.
 * @param product - Current product info.
 * @param catalogId - Current catalog.
 * @returns React element for the specific product.
 */
function renderProductComparisonButton(
    productComparisonButton: React.ReactNode,
    product: ProductSearchResult,
    catalogId: number
): JSX.Element | undefined {
    validateCatalogId(catalogId);
    return React.cloneElement(productComparisonButton as React.ReactElement, { product, catalogId });
}

/**
 * Gets the product page url from the default swatch selected.
 * @param  coreContext - Context of the module using the component.
 * @param productUrl - Product page url for the product card.
 * @param  productData - Product card to be rendered.
 * @returns The product card image url.
 */
function getProductPageUrlFromDefaultSwatch(
    coreContext: ICoreContext,
    productUrl: string,
    productData?: ProductSearchResult
): string | undefined {
    const defaultSwatch = getDefaultSwatchSelected(coreContext, productData);
    if (!defaultSwatch?.SwatchValue) {
        return productUrl;
    }

    const siteContext = coreContext as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;

    const queryStringEncoded = encodeURIComponent(defaultSwatch.SwatchValue);
    const queryString = `${dimensionToPreSelectInProductCard}=${queryStringEncoded}`;
    return updateProductUrl(productUrl, coreContext, queryString);
}

const renderCatcWeightQuantity = (
    props: IProductComponentProps,
    physicalQuantity: number | undefined,
    product: ProductSearchResult,
    catchWeightQuantity: number,
    setCatchWeightQuantity: (val: number) => void
) => {
    const values = product?.ExtensionProperties ?? [];
    const conversionFactorAttribute = values.find(attribute => attribute.Key === 'ConversionFactor')?.Value;
    const cwUnitAttribute = values.find(attribute => attribute.Key === 'CWUnit')?.Value;
    const conversionFactorValue = conversionFactorAttribute?.DecimalValue ?? 0;
    const cwUnitValue = cwUnitAttribute?.StringValue;

    if (!physicalQuantity || conversionFactorValue <= 0 || !cwUnitValue) {
        return <></>;
    }
    let maxQuantity = Math.floor(physicalQuantity / conversionFactorValue);
    console.log(props);

    return (
        <>
            <div className='ms-buybox productQuantityCenter'>
                <div className='quantity' id='ms-buybox__product-quantity-input'>
                    <button
                        title=''
                        className='decrement quantity__controls disabled'
                        aria-hidden='true'
                        aria-label='Press to decrease the quantity by 1'
                        color='secondary'
                        onClick={() => {
                            if (catchWeightQuantity === 1) {
                                return;
                            } else {
                                setCatchWeightQuantity(--catchWeightQuantity);
                            }
                        }}
                    >
                        <span className='fas fa-minus quantity__controls-glyph' />
                    </button>
                    <input
                        type='number'
                        className='quantity-input'
                        pattern='[0-9]*'
                        defaultValue={'1'}
                        aria-live='polite'
                        aria-label='quantity input'
                        role='spinbutton'
                        value={catchWeightQuantity}
                        aria-valuemin={1}
                        aria-valuemax={maxQuantity}
                        aria-valuenow={1}
                        min={1}
                        max={maxQuantity}
                        onChange={e => {
                            const rawValue = e.target.value;
                            if (rawValue === '') {
                                setCatchWeightQuantity(1);
                                return;
                            }
                            const value = parseInt(rawValue, 10);
                            if (isNaN(value)) {
                                setCatchWeightQuantity(1);
                                return;
                            }
                            const clampedValue = Math.min(Math.max(value, 1), maxQuantity);
                            setCatchWeightQuantity(clampedValue);
                        }}
                    />
                    <button
                        title='Press to increment quantity by 1'
                        className='increment quantity__controls '
                        aria-hidden='false'
                        aria-label='Press to increment quantity by 1'
                        color='secondary'
                        onClick={() => {
                            if (catchWeightQuantity < maxQuantity) {
                                setCatchWeightQuantity(++catchWeightQuantity);
                            } else {
                                console.log('Max Quantity reached');
                                return;
                            }
                        }}
                    >
                        <span className='fas fa-plus quantity__controls-glyph' />
                    </button>
                </div>
            </div>
        </>
    );
};

const ProductCard: React.FC<IProductComponentProps> = props => {
    const {
        data,
        context,
        imageSettings,
        savingsText,
        freePriceText,
        originalPriceText,
        currentPriceText,
        ratingAriaLabel,
        ratingCountAriaLabel,
        allowBack,
        typeName,
        id,
        telemetryContent,
        quickViewButton,
        productComparisonButton,
        isEnabledProductDescription,
        isPriceMinMaxEnabled,
        priceResources,
        inventoryLabel,
        dimensionAvailabilities,
        swatchItemAriaLabel
        // resources
    } = props;
    const product = data.product;

    const counterRef = React.useRef<any>(null);

    let productUrl = getProductPageUrlSync(product?.Name ?? '', product?.RecordId ?? Number.MIN_VALUE, context.actionContext, undefined);
    if (allowBack) {
        productUrl = updateProductUrl(productUrl, context, 'back=true');
    }
    const productImageUrlFromSwatch = getProductImageUrlFromDefaultSwatch(context, product) ?? product?.PrimaryImageUrl;
    const productPageUrlFromSwatch = getProductPageUrlFromDefaultSwatch(context, productUrl, product) ?? productUrl;
    const [productPageUrl, setProductPageUrl] = useState<string>(productPageUrlFromSwatch);
    const [productImageUrl, setProductImageUrl] = useState<string | undefined>(productImageUrlFromSwatch);
    const [unitOfMeasureData, setUnitOfMeasureData] = React.useState<
        { unitOfMeasureDropDownName: string; unitOfMeasureDropDownValue: string }[] | undefined
    >([]);
    const [selectedUnit, setSelectedUnit] = React.useState<string>('');
    const [newUpdatedPrice, setNewUpdatedPrice] = React.useState<ProductPrice | undefined>({});
    const productPrice = newUpdatedPrice;
    const [isStoreSelected, setStoreSelected] = React.useState<boolean>(false);
    const [storeId, setStoreId] = React.useState<string>('');
    const [availabilityInStore, setAvailabilityInStore] = React.useState<boolean>(false);
    const [physicalQuantity, setPhysicalQuantity] = React.useState<number | undefined>(context.app.config.maxQuantityForCartLineItem);

    const [cwUnitValue] = React.useState<string>(getAttributeValue('CWUnit') as string);
    const [ConversionFactorValue] = React.useState<number | null>(getAttributeValue('ConversionFactor') as number | null);
    const [catchWeightQuantity, setCatchWeightQuantity] = React.useState<number>(1);
    const [pricePerlb, setPricePerlb] = useState<number | string>('');
    const [brand, setBrand] = React.useState<string>('');
    const [packSize] = React.useState<string | null>(getAttributeValue('PackSize') as string | null);
    const [packDescription] = React.useState<string | null>(getAttributeValue('SizeDescription') as string | null);
    const [isSuspended] = React.useState<boolean | null>(getAttributeValue('ItemSuperseded') as boolean | null);
    const [isOriginalItem] = React.useState<string | null>(getAttributeValue('OriginalItem') as string | null);

    function getAttributeValue(key: string): boolean | string | number | null {
        const values = product?.ExtensionProperties ?? [];

        const attribute = values.find(attribute => attribute.Key === key)?.Value;

        if (!attribute) {
            return false;
        }

        const valueMap: Record<string, boolean | string | number | null> = {
            DualUOMEnabled: attribute.BooleanValue ?? false,
            CWUnit: attribute.StringValue ?? '',
            ConversionFactor: attribute.DecimalValue ?? null,
            PackSize: attribute.StringValue ?? '',
            SizeDescription: attribute.StringValue ?? '',
            ItemSuperseded: attribute.BooleanValue ?? false,
            OriginalItem: attribute.StringValue ?? ''
        };

        return valueMap[key] ?? false;
    }

    const getProductAtt = async () => {
        if (product?.RecordId) {
            const productAtt = await getAttributeValuesAsync(
                { callerContext: context.actionContext },
                product.RecordId,
                props.context.request.apiSettings.channelId,
                props.context.request.apiSettings.catalogId
            );
            const brand = productAtt.find(item => item.Name === 'Brand');
            setBrand(brand?.TextValue ?? '');
        }
    };

    React.useEffect(() => {
        getProductAtt();
    }, []);

    React.useEffect(() => {
        if (MsDyn365.isBrowser && typeof window !== 'undefined' && localStorage.getItem('_msdyn365__preferredStore_') !== null) {
            const storedValue = localStorage.getItem('_msdyn365__preferredStore_');
            if (storedValue) {
                const parsedValue: IStoreDataCustom = JSON.parse(storedValue);
                if (parsedValue.StoreId) {
                    setStoreSelected(true);
                    setStoreId(parsedValue.StoreId);
                }
            }
        }
    }, []);

    const getInventory_ = async () => {
        if (!context.app.config.enableStockCheck) {
            setAvailabilityInStore(true);
        } else if (product?.RecordId) {
            const searchArea: SearchArea = {
                Latitude: 0,
                Longitude: 0,
                Radius: 0,
                DistanceUnitValue: 0
            };
            const searchCriteria = createInventoryAvailabilitySearchCriteria(
                context.actionContext,
                [product.RecordId],
                false,
                true,
                searchArea,
                2
            );
            const isItemInStock = (availibilityInWarehouse: ProductWarehouseInventoryAvailability): boolean => {
                if (!context.app.config.enableStockCheck) {
                    return true;
                }
                if (availibilityInWarehouse.PhysicalAvailable) {
                    if (ConversionFactorValue && cwUnitValue) {
                        const getCWReservePhysicalQuantity = (availibilityInWarehouse: ProductWarehouseInventoryAvailability): number =>
                            availibilityInWarehouse?.ExtensionProperties?.find(({ Key }) => Key === 'CWReservePhysicalQuantity')?.Value
                                ?.DecimalValue ?? 0;
                        let x = getCWReservePhysicalQuantity(availibilityInWarehouse);
                        const quantity = availibilityInWarehouse.PhysicalAvailable - x * ConversionFactorValue;
                        setPhysicalQuantity(quantity);

                        if (quantity > ConversionFactorValue) {
                            return true;
                        }
                    } else {
                        setPhysicalQuantity(availibilityInWarehouse.PhysicalAvailable);
                        return !!(availibilityInWarehouse.PhysicalAvailable > BuyBoxConstants.zero);
                    }
                }

                return false;
            };

            await getEstimatedAvailabilityAsync({ callerContext: context.actionContext }, searchCriteria).then(response => {
                const availibilityInWarehouse = response?.ProductWarehouseInventoryAvailabilities?.find(
                    item => item.InventLocationId === storeId
                );
                if (availibilityInWarehouse) {
                    const isInStock = isItemInStock(availibilityInWarehouse);
                    setAvailabilityInStore(isInStock);
                }
                console.log(response, availibilityInWarehouse);
            });
        }
    };
    React.useEffect(() => {
        if (storeId && product?.RecordId) {
            getInventory_().catch((error: Error) => {
                console.error(error.message);
            });
        }
    }, [product?.RecordId, storeId]);

    const getProductUnitOfMeasureFunc = async () => {
        const getProductUnitOfMeasureInput = new GetProductUnitOfMeasureInput(product?.RecordId, {
            IncludeQuantityLimitRules: true
        });
        const result = await getProductUnitsOfMeasureAction(getProductUnitOfMeasureInput, context.actionContext);
        if (result) {
            const preferredStore = JSON.parse(localStorage.getItem('_msdyn365__preferredStore_') || '');
            let filteredData: any = [];
            if (preferredStore && preferredStore.StoreId) {
                filteredData = result
                    ?.filter((item: any) => {
                        // Find UOMAvailableStores extension property
                        const uomStoreProp = item.ExtensionProperties?.find((prop: any) => prop.Key === 'UOMAvailableStores');

                        // If UOMAvailableStores does NOT exist → include item
                        if (!uomStoreProp?.Value?.StringValue) {
                            return true;
                        }

                        try {
                            // Parse StringValue
                            const stores = JSON.parse(uomStoreProp.Value.StringValue);

                            // If current storeId exists in PickupStore → exclude item
                            const isAvailableInStore = stores.some((store: any) => store.PickupStore === preferredStore.StoreId);

                            return !isAvailableInStore;
                        } catch (e) {
                            // If parsing fails, fail-safe → include item
                            return true;
                        }
                    })
                    ?.filter((item: any) => item.Description === 'Case' || item.Description === 'Each')
                    .map((item: any) => ({
                        unitOfMeasureDropDownName: item.Description,
                        unitOfMeasureDropDownValue: item.Symbol
                    }));
            } else {
                filteredData = result
                    ?.filter((item: any) => item.Description === 'Case' || item.Description === 'Each') // Filter condition
                    .map((item: any) => ({
                        unitOfMeasureDropDownName: item.Description,
                        unitOfMeasureDropDownValue: item.Symbol
                    }));
            }
            setUnitOfMeasureData(prevState => {
                const newState = filteredData;
                return newState;
            });
            if (filteredData && filteredData.length) {
                setSelectedUnit(filteredData[0].unitOfMeasureDropDownValue);
            }
        }
    };

    const projectDomain: ProjectionDomain = {
        ChannelId: context.request.apiSettings.channelId,
        CatalogId: context.request.apiSettings.catalogId
    };

    const getActivePricesInputMeasure: PriceLookupContext | null = {
        HeaderContext: undefined,
        LineContexts: [
            {
                ProductRecordId: product?.RecordId,
                UnitOfMeasureSymbol: selectedUnit
            }
        ]
    };

    const getActivePricesFunc = async () => {
        try {
            const getActivePricesInput = new GetActivePricesInput(
                projectDomain,
                undefined,
                undefined,
                undefined,
                undefined,
                true,
                false,
                false,
                getActivePricesInputMeasure
            );
            if (selectedUnit) {
                const result = await getActivePricesAction(getActivePricesInput, context.actionContext);
                if (result && result.length > 0) {
                    if (result[0]?.CustomerContextualPrice && ConversionFactorValue) {
                        setPricePerlb((result[0].CustomerContextualPrice / ConversionFactorValue).toFixed(2));
                    }
                    setNewUpdatedPrice(result[0]);
                } else {
                    console.warn('No active prices returned from API');
                }
            }
        } catch (error) {
            console.error('Error fetching active prices:', error);
        }
    };

    React.useEffect(() => {
        getProductUnitOfMeasureFunc();
    }, []);

    React.useEffect(() => {
        getActivePricesFunc();
    }, [selectedUnit]);
    React.useEffect(() => {
        setProductPageUrl(productPageUrlFromSwatch);
        setProductImageUrl(productImageUrlFromSwatch);
    }, [productUrl, productPageUrlFromSwatch, productImageUrlFromSwatch]);
    const [selectedSwatchItems] = useState(new Dictionary<DimensionTypes, ISwatchItem>());
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access -- app context is generic
    const enableStockCheck = context.app.config.enableStockCheck;

    const siteContext = context as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;

    /**
     * Updates the product page and Image url based on swatch selected.
     * @param coreContext - Context of the caller.
     * @param swatchItem - Dimension swatch selected.
     */
    const updatePageAndImageUrl = React.useCallback(
        (coreContext: ICoreContext, swatchItem: ISwatchItem) => {
            const dimensionType = swatchItem.dimensionType;
            selectedSwatchItems.setValue(dimensionType, swatchItem);
            if (StringExtensions.isNullOrWhitespace(swatchItem.value)) {
                return;
            }
            const queryString = `${dimensionType}=${swatchItem.value}`;
            let productPageUrlWithSwatch = '';
            if (productPageUrl.includes(dimensionType)) {
                const newUrl = new URL(productPageUrl, coreContext.request.apiSettings.baseUrl);
                newUrl.searchParams.delete(dimensionType);
                productPageUrlWithSwatch = updateProductUrl(newUrl.toString(), context, queryString);
            } else {
                productPageUrlWithSwatch = updateProductUrl(productPageUrl, context, queryString);
            }
            setProductPageUrl(productPageUrlWithSwatch);
            if (dimensionType === dimensionToPreSelectInProductCard) {
                const swatchProductImageUrl = ArrayExtensions.hasElements(swatchItem.productImageUrls)
                    ? swatchItem.productImageUrls[0]
                    : undefined;
                const newImageUrl = generateImageUrl(swatchProductImageUrl, coreContext.request.apiSettings);
                setProductImageUrl(newImageUrl);
            }
        },
        [selectedSwatchItems, context, productPageUrl]
    );

    if (!product) {
        return null;
    }

    /**
     * Checks if rendering the particular dimensions is allowed for product card.
     * @param dimensionType - Dimension to be displayed.
     * @returns Updates the state with new product page url.
     */
    function shouldDisplayDimension(dimensionType: string): boolean {
        const dimensionsContext = context as ICoreContext<IDimensionsApp>;
        const dimensionsToDisplayOnProductCard = dimensionsContext.app.config.dimensionsInProductCard;
        return (
            ArrayExtensions.hasElements(dimensionsToDisplayOnProductCard) &&
            !dimensionsToDisplayOnProductCard.includes(DimensionTypes.none) &&
            dimensionsToDisplayOnProductCard.includes(dimensionType.toLocaleLowerCase() as DimensionTypes)
        );
    }

    /**
     * Gets the react node for product dimension as swatch.
     * @param  attributeValues - Attribute value property from product.
     * @returns The node representing markup for unit of measure component.
     */
    function renderProductDimensions(attributeValues?: AttributeValue[]): JSX.Element | null {
        if (!attributeValues) {
            return null;
        }

        return (
            <div className='msc-product__dimensions'>
                {attributeValues.map((item: AttributeValue) => {
                    const dimensionTypeValue = item.KeyName?.toLocaleLowerCase() ?? '';
                    if (!shouldDisplayDimension(dimensionTypeValue)) {
                        return null;
                    }

                    const siteContext = context as ICoreContext<IDimensionsApp>;
                    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;
                    const dimensionType = dimensionTypeValue as DimensionTypes;
                    const swatches =
                        item.Swatches?.map<ISwatchItem>(swatchItem => {
                            return {
                                itemId: `${item.RecordId ?? ''}-${dimensionTypeValue}-${swatchItem.SwatchValue ?? ''}`,
                                value: swatchItem.SwatchValue ?? '',
                                dimensionType,
                                colorHexCode: swatchItem.SwatchColorHexCode,
                                imageUrl: swatchItem.SwatchImageUrl,
                                productImageUrls: swatchItem.ProductImageUrls,
                                isDefault: swatchItem.IsDefault,
                                swatchItemAriaLabel: swatchItemAriaLabel ? format(swatchItemAriaLabel, dimensionType) : '',
                                isDisabled:
                                    enableStockCheck &&
                                    dimensionAvailabilities?.find(
                                        dimensionAvailability => dimensionAvailability.value === (swatchItem.SwatchValue ?? '')
                                    )?.isDisabled
                            };
                        }) ?? [];
                    if (
                        dimensionType === dimensionToPreSelectInProductCard &&
                        ArrayExtensions.hasElements(swatches) &&
                        !swatches.some(swatch => swatch.isDefault)
                    ) {
                        swatches[0].isDefault = true;
                    }
                    return (
                        <ProductComponentSwatchComponent
                            context={context}
                            swatches={swatches}
                            onSelectDimension={updatePageAndImageUrl}
                            key={item.RecordId}
                        />
                    );
                })}
            </div>
        );
    }

    function renderQuickView(quickview: React.ReactNode, item?: number): JSX.Element | undefined {
        if (quickview === null) {
            return undefined;
        }
        const selectedDimensions: ProductDimension[] = selectedSwatchItems.getValues().map<ProductDimension>(swatches => {
            return {
                DimensionTypeValue: convertDimensionTypeToProductDimensionType(swatches.dimensionType),
                DimensionValue: {
                    RecordId: 0,
                    Value: swatches.value
                }
            };
        });
        return React.cloneElement(quickview as React.ReactElement, { selectedProductId: item, selectedDimensions });
    }

    const addToCartCustom = async (
        selectedUnit: string,
        currentQuantity: number,
        cwUnitValue: string,
        ConversionFactorValue: number | null
    ) => {
        const cartState = await getCartState(context.actionContext);
        // const cartId = result?.cart?.Id!;
        const addProductToCart: CartLine[] = [];
        if (product) {
            const createCartItem = () => ({
                CatalogId: context.request.apiSettings.catalogId,
                DeliveryMode: 'BOPIS',
                FulfillmentStoreId: storeId,
                Quantity: ConversionFactorValue ? ConversionFactorValue : currentQuantity,
                ItemId: product.ItemId,
                ProductId: product.RecordId,
                UnitOfMeasureSymbol: cwUnitValue ? product.DefaultUnitOfMeasure : selectedUnit,
                WarehouseId: storeId,
                IsGiftCardLine: false,
                Price: product.Price,
                EntryMethodTypeValue: 3,
                TrackingId: ''
            });

            if (product.DefaultUnitOfMeasure === 'lb') {
                for (let i = 0; i < catchWeightQuantity; i++) {
                    addProductToCart.push(createCartItem());
                }
            } else {
                addProductToCart.push(createCartItem());
            }
        }

        await addCartLinesAsync({ callerContext: context.actionContext }, cartState.cart.Id, addProductToCart)
            .then(async newCart => {
                await cartState.updateCart({ newCartObject: newCart });
            })
            .catch(error => {
                console.log(error.data.LocalizedMessage ?? '');
                // setLimitErrorMessage(error.data.LocalizedMessage ?? '');
            });
    };

    // Construct telemetry attribute to render
    const payLoad = getPayloadObject('click', telemetryContent!, '', product.RecordId.toString());

    const attribute = getTelemetryAttributes(telemetryContent!, payLoad);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- -- Do not need type check for appsettings
    const isUnitOfMeasureEnabled = context.app.config && context.app.config.unitOfMeasureDisplayType === 'buyboxAndBrowse';
    const user = context?.request?.user;
    const handleDropdownChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        event.stopPropagation();
        setSelectedUnit(event.target.value);
    };

    const getmaxQuantity = (): number => {
        return physicalQuantity ?? maxQuantity;
    };

    const maxQuantity = context.app.config.maxQuantityForCartLineItem;
    const [currentQuantity, setCurrentQuantity] = React.useState(1);
    const maxQuantityText = 'The maximum quantity to purchase for this product is {max}.'.replace('{max}', getmaxQuantity().toString());

    const onQuantityChange = (value: number) => {
        setCurrentQuantity(value);
        return true;
    };
    const handleClickAddTocart = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        addToCartCustom(selectedUnit, currentQuantity, cwUnitValue, ConversionFactorValue);
    };

    React.useEffect(() => {
        const interval = setInterval(() => {
            if (counterRef?.current) {
                const currentValue = counterRef?.current?.inputRef?.current?.value;
                if (currentQuantity && currentValue !== currentQuantity.toString()) {
                    if (currentValue === '') {
                        setCurrentQuantity(1);
                    }
                }
            }
        }, 300);

        return () => clearInterval(interval);
    }, [currentQuantity]);

    const getCurrentCountValue = (): number | string | null => {
        if (ConversionFactorValue && currentQuantity) {
            return ConversionFactorValue * currentQuantity;
        } else {
            return ConversionFactorValue;
        }
    };
    const showOutOfStock = (enableStockCheck: boolean): string => {
        if (!(unitOfMeasureData && unitOfMeasureData?.length > 0)) return 'Out of Stock';
        if (!enableStockCheck) return 'Add to cart';
        if (!availabilityInStore) return 'Out of Stock';
        return 'Add to cart';
    };

    const suspendedDesc = () => {
        if (isSuspended) {
            return (
                <div className='superceded'>
                    <p>
                        {isOriginalItem} Phased out, is replaced by <span>{product.ItemId}</span>
                    </p>
                </div>
            );
        } else {
            return <></>;
        }
    };

    const brandAndSizeInfoBox = () => {
        return (
            <div style={{ fontSize: '12px' }}>
                <div>
                    <div style={{ textAlignLast: 'left' }}>
                        <span>
                            <strong>Brand: </strong> {brand}
                        </span>
                    </div>

                    <div style={{ textAlignLast: 'left' }}>
                        <span>
                            <strong>Size: </strong> {packSize} / {packDescription}{' '}
                            {product.DefaultUnitOfMeasure === 'ea' ? 'each' : product.DefaultUnitOfMeasure}
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            {isEnabledProductDescription ? (
                <>
                    <a
                        href={productPageUrl}
                        onClick={onTelemetryClick(telemetryContent!, payLoad, product.Name!)}
                        aria-label={renderLabel(
                            product.Name,
                            context.cultureFormatter.formatCurrency(product.Price),
                            product.AverageRating,
                            ratingAriaLabel,
                            product.TotalRatings,
                            ratingCountAriaLabel
                        )}
                        className='msc-product'
                        {...attribute}
                    >
                        <div className='msc-product__image__description'>
                            <div className='msc-product__image'>
                                {renderProductPlacementImage(
                                    imageSettings,
                                    context.request.gridSettings,
                                    productImageUrl,
                                    product.PrimaryImageUrl,
                                    product.Name,
                                    context
                                )}
                            </div>
                        </div>
                    </a>
                    <div className='plp-sku'>
                        <span>SUPC:</span> {`${product.ItemId}`}
                    </div>
                    {suspendedDesc()}
                    <a className='msc-product'>
                        <div className='msc-product__title_description'>
                            <h2 className='msc-product__title__text'>{product.Name}</h2>
                            {/* <label htmlFor='unitOfMeasureDropdown'>Select Unit of Measure:</label> */}
                            {!cwUnitValue && !ConversionFactorValue && (
                                <select
                                    id='unitOfMeasureDropdown'
                                    value={selectedUnit}
                                    onClick={event => event.stopPropagation()}
                                    onChange={handleDropdownChange}
                                    style={{ padding: '8px', margin: '8px 0', width: '100%' }}
                                >
                                    {/* <option value='' disabled>
                                    -- Select Unit of Measure --
                                </option> */}
                                    {unitOfMeasureData?.map(unit => (
                                        <option key={unit.unitOfMeasureDropDownValue} value={unit.unitOfMeasureDropDownValue}>
                                            {unit.unitOfMeasureDropDownName}
                                        </option>
                                    ))}
                                </select>
                            )}
                            {cwUnitValue && ConversionFactorValue && (
                                <div className='msc-product__uom-CW'>
                                    <div className='msc-product__uom-CW__wrapper'>
                                        <div>Catch Weight Unit</div>
                                        <div className='borderBox'>{cwUnitValue}</div>
                                    </div>
                                    <div className='msc-product__uom-CW__wrapper'>
                                        <div>Catch Weight lbs</div>
                                        <div className='borderBox'>{getCurrentCountValue()}</div>
                                    </div>
                                </div>
                            )}
                            {user?.isAuthenticated ? (
                                <>
                                    {renderPrice(
                                        context,
                                        typeName,
                                        id,
                                        product,
                                        savingsText,
                                        freePriceText,
                                        originalPriceText,
                                        currentPriceText,
                                        isPriceMinMaxEnabled,
                                        priceResources,
                                        productPrice
                                    )}
                                    {/* {isUnitOfMeasureEnabled && renderProductUnitOfMeasure(product.DefaultUnitOfMeasure)} */}
                                    {isUnitOfMeasureEnabled && renderProductUnitOfMeasure(selectedUnit)}
                                    {cwUnitValue && ConversionFactorValue ? <div className='pricePerLb'>(${pricePerlb}/lb)</div> : null}
                                    {renderDescription(product.Description)}
                                    <div>
                                        {!cwUnitValue && !ConversionFactorValue && (
                                            <div className='ms-buybox productQuantityCenter'>
                                                <IncrementalQuantity
                                                    ref={counterRef}
                                                    id='ms-buybox__product-quantity-input'
                                                    max={getmaxQuantity()}
                                                    currentCount={currentQuantity}
                                                    onChange={onQuantityChange}
                                                    inputQuantityAriaLabel={'quantity input'}
                                                    decrementButtonAriaLabel={'Press to decrease the quantity by 1'}
                                                    incrementButtonAriaLabel={'Press to increment quantity by 1'}
                                                    minQuantityText={'The minimum quantity to purchase for this product is 1.'}
                                                    maxQuantityText={maxQuantityText}
                                                    telemetryContent={telemetryContent}
                                                    disabled={cwUnitValue && ConversionFactorValue ? true : false}
                                                />
                                            </div>
                                        )}
                                        <span>
                                            <div className='ms-buybox__add-to-cart-container'>
                                                <button
                                                    className='msc-add-to-cart '
                                                    aria-label={isStoreSelected ? 'Add to cart' : 'Please Select Preferred Store Locator'}
                                                    title={isStoreSelected ? 'Add to cart' : 'Please Select Preferred Store Locator'}
                                                    data-exp-event-id='Default product details.buybox.Add to cart.AddToCart'
                                                    onClick={handleClickAddTocart}
                                                    disabled={!isStoreSelected}
                                                    style={{ cursor: isStoreSelected ? 'pointer' : 'not-allowed' }}
                                                >
                                                    {showOutOfStock(context.app.config.enableStockCheck)}
                                                </button>
                                            </div>
                                        </span>
                                    </div>
                                </>
                            ) : (
                                <a href={user?.signInUrl} className='login-register-link'>
                                    {'Sign in to see price'}
                                </a>
                            )}
                        </div>
                    </a>
                </>
            ) : (
                <>
                    <a
                        href={productPageUrl}
                        onClick={onTelemetryClick(telemetryContent!, payLoad, product.Name!)}
                        aria-label={renderLabel(
                            product.Name,
                            context.cultureFormatter.formatCurrency(product.Price),
                            product.AverageRating,
                            ratingAriaLabel
                        )}
                        className='msc-product'
                        {...attribute}
                    >
                        <div className='msc-product__image'>
                            {renderProductPlacementImage(
                                imageSettings,
                                context.request.gridSettings,
                                productImageUrl,
                                product.PrimaryImageUrl,
                                product.Name,
                                context
                            )}
                        </div>
                    </a>
                    <div className='plp-sku'>
                        <span>SUPC:</span> {`${product.ItemId}`}
                    </div>
                    {suspendedDesc()}
                    <a className='msc-product'>
                        <div className='msc-product__details'>
                            <h2 className='msc-product__title'>{product.Name}</h2>
                            {user?.isAuthenticated ? (
                                <>
                                    {/* <label htmlFor='unitOfMeasureDropdown'>Select Unit of Measure:</label> */}
                                    {!cwUnitValue && !ConversionFactorValue && (
                                        <select
                                            id='unitOfMeasureDropdown'
                                            value={selectedUnit}
                                            onClick={event => event.stopPropagation()}
                                            onChange={handleDropdownChange}
                                            style={{ padding: '8px', margin: '8px 0', width: '100%' }}
                                        >
                                            {unitOfMeasureData?.map(unit => (
                                                <option key={unit.unitOfMeasureDropDownValue} value={unit.unitOfMeasureDropDownValue}>
                                                    {unit.unitOfMeasureDropDownName}
                                                </option>
                                            ))}
                                        </select>
                                    )}

                                    {cwUnitValue && ConversionFactorValue && (
                                        <div className='msc-product__uom-CW'>
                                            <div className='msc-product__uom-CW__wrapper'>
                                                <div>Catch Weight Unit</div>
                                                <div className='borderBox'>{cwUnitValue}</div>
                                            </div>
                                            <div className='msc-product__uom-CW__wrapper'>
                                                <div>Catch Weight lbs</div>
                                                <div className='borderBox'>{getCurrentCountValue()}</div>
                                            </div>
                                        </div>
                                    )}

                                    {renderPrice(
                                        context,
                                        typeName,
                                        id,
                                        product,
                                        savingsText,
                                        freePriceText,
                                        originalPriceText,
                                        currentPriceText,
                                        isPriceMinMaxEnabled,
                                        priceResources,
                                        productPrice
                                    )}
                                    {/* {isUnitOfMeasureEnabled && renderProductUnitOfMeasure(product.DefaultUnitOfMeasure)} */}
                                    {isUnitOfMeasureEnabled && renderProductUnitOfMeasure(selectedUnit)}
                                    {cwUnitValue && ConversionFactorValue ? <div className='pricePerLb'>(${pricePerlb}/lb)</div> : null}
                                    <div>
                                        {!cwUnitValue && !ConversionFactorValue && (
                                            <div className='ms-buybox productQuantityCenter'>
                                                <IncrementalQuantity
                                                    ref={counterRef}
                                                    id='ms-buybox__product-quantity-input'
                                                    max={getmaxQuantity()}
                                                    currentCount={currentQuantity}
                                                    onChange={onQuantityChange}
                                                    inputQuantityAriaLabel={'quantity input'}
                                                    decrementButtonAriaLabel={'Press to decrease the quantity by 1'}
                                                    incrementButtonAriaLabel={'Press to increment quantity by 1'}
                                                    minQuantityText={'The minimum quantity to purchase for this product is 1.'}
                                                    maxQuantityText={maxQuantityText}
                                                    telemetryContent={telemetryContent}
                                                    disabled={cwUnitValue && ConversionFactorValue ? true : false}
                                                />
                                            </div>
                                        )}

                                        {cwUnitValue && ConversionFactorValue && (
                                            <>
                                                {renderCatcWeightQuantity(
                                                    props,
                                                    physicalQuantity,
                                                    product,
                                                    catchWeightQuantity,
                                                    setCatchWeightQuantity
                                                )}
                                            </>
                                        )}

                                        <span>
                                            <div className='ms-buybox__add-to-cart-container'>
                                                <button
                                                    className='msc-add-to-cart '
                                                    aria-label={isStoreSelected ? 'Add to cart' : 'Please Select Preferred Store Locator'}
                                                    title={isStoreSelected ? 'Add to cart' : 'Please Select Preferred Store Locator'}
                                                    data-exp-event-id='Default product details.buybox.Add to cart.AddToCart'
                                                    onClick={handleClickAddTocart}
                                                    disabled={
                                                        isStoreSelected &&
                                                        showOutOfStock(context.app.config.enableStockCheck) === 'Add to cart'
                                                            ? false
                                                            : true
                                                    }
                                                    style={{ cursor: isStoreSelected ? 'pointer' : 'not-allowed' }}
                                                >
                                                    {showOutOfStock(context.app.config.enableStockCheck)}
                                                </button>
                                            </div>
                                        </span>
                                    </div>
                                </>
                            ) : (
                                <a href={user?.signInUrl} className='login-register-link'>
                                    {'Sign in to see price'}
                                </a>
                            )}
                        </div>
                    </a>
                </>
            )}
            {brandAndSizeInfoBox()}
            {renderProductDimensions(product.AttributeValues)}
            {!context.app.config.hideRating &&
                renderRating(context, typeName, id, product.AverageRating, product.TotalRatings, ratingAriaLabel, ratingCountAriaLabel)}
            {renderProductAvailability(inventoryLabel)}
            {quickViewButton && renderQuickView(quickViewButton, product.RecordId)}
            {productComparisonButton && renderProductComparisonButton(productComparisonButton, product, getCatalogId(context.request))}
        </>
    );
};

function renderLabel(
    name?: string,
    price?: string,
    rating?: number,
    ratingAriaLabelText?: string,
    reviewCount?: number,
    ratingCountAriaLabelText?: string
): string {
    const reviewCountArialableText = getReviewAriaLabel(reviewCount, ratingCountAriaLabelText ?? '');
    return `${name ?? ''} ${price ?? ''} ${getRatingAriaLabel(rating, ratingAriaLabelText)}${
        reviewCountArialableText ? ` ${reviewCountArialableText}` : ''
    }`;
}

function renderDescription(description?: string): JSX.Element | null {
    return <p className='msc-product__text'>{description}</p>;
}

function getRatingAriaLabel(rating?: number, ratingAriaLabel?: string): string {
    if (rating && ratingAriaLabel) {
        const roundedRating = rating.toFixed(2);
        return format(ratingAriaLabel || '', roundedRating, '5');
    }
    return '';
}

function getReviewAriaLabel(reviewCount?: number, ratingCountAriaLabelText?: string): string {
    if (reviewCount && ratingCountAriaLabelText) {
        return format(ratingCountAriaLabelText || '', reviewCount);
    }
    return '';
}

function renderRating(
    coreContext: ICoreContext,
    moduleTypeName: string,
    moduleId: string,
    avgRating?: number,
    totalRatings?: number,
    ariaLabel?: string,
    ratingCountAriaLabel?: string
): JSX.Element | null {
    if (!avgRating) {
        return null;
    }

    const numberRatings = totalRatings?.toString() || undefined;
    const ratingAriaLabelText = getRatingAriaLabel(avgRating, ariaLabel);
    const ratingCountAriaLabelText = getReviewAriaLabel(Number(numberRatings), ratingCountAriaLabel);

    return (
        <RatingComponent
            context={coreContext}
            id={moduleId}
            typeName={moduleTypeName}
            avgRating={avgRating}
            ratingCount={numberRatings}
            readOnly
            ariaLabel={ratingAriaLabelText}
            ratingCountAriaLabel={ratingCountAriaLabelText}
            data={{}}
        />
    );
}

function renderPrice(
    context: ICoreContext,
    typeName: string,
    id: string,
    productSearchResult: ProductSearchResult,
    savingsText?: string,
    freePriceText?: string,
    originalPriceText?: string,
    currentPriceText?: string,
    isPriceMinMaxEnabled?: boolean,
    priceResources?: IPriceComponentResources,
    productPrice?: ProductPrice
): JSX.Element | null {
    const product: SimpleProduct = {
        // This is a fake product. The product type is only used for price component to determine if it should show price range or not.
        ProductTypeValue: productSearchResult.IsMasterProduct ? ProductType.Master : ProductType.Standalone
    } as SimpleProduct;

    const price: ProductPrice = productPrice ?? {
        BasePrice: productSearchResult.BasePrice,
        AdjustedPrice: productSearchResult.Price,
        CustomerContextualPrice: productSearchResult.Price,
        MaxVariantPrice: productSearchResult.MaxVariantPrice ? productSearchResult.MaxVariantPrice : productSearchResult.Price,
        MinVariantPrice: productSearchResult.MinVariantPrice ? productSearchResult.MinVariantPrice : productSearchResult.Price
    };

    return (
        <PriceComponent
            context={context}
            id={id}
            typeName={typeName}
            product={product}
            data={{ price }}
            savingsText={savingsText}
            freePriceText={freePriceText}
            originalPriceText={originalPriceText}
            isPriceMinMaxEnabled={isPriceMinMaxEnabled}
            priceResources={priceResources}
        />
    );
}

function renderProductPlacementImage(
    imageSettings?: IImageSettings,
    gridSettings?: IGridSettings,
    imageUrl?: string,
    fallbackImageUrl?: string,
    altText?: string,
    context?: ICoreContext<IGeneric<IAny>>
): JSX.Element | null {
    if (!imageUrl || !gridSettings || !imageSettings) {
        return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- Site level config can be of any type.
    const contextConfig = context?.app.config?.placeholderImageName;
    const emptyPlaceHolderImage = contextConfig as string;
    let fallbackImageSource = fallbackImageUrl;
    if (emptyPlaceHolderImage && fallbackImageUrl) {
        fallbackImageSource = `${fallbackImageUrl},${emptyPlaceHolderImage}`;
    }
    const img: IImageData = {
        src: imageUrl,
        altText: altText ? altText : '',
        fallBackSrc: fallbackImageSource
    };
    const imageProps = {
        gridSettings,
        imageSettings
    };
    imageProps.imageSettings.cropFocalRegion = true;
    return (
        <Image
            requestContext={context?.actionContext.requestContext}
            {...img}
            {...imageProps}
            loadFailureBehavior='empty'
            bypassHideOnFailure
        />
    );
}

export const ProductComponent: React.FunctionComponent<IProductComponentProps> = msdyn365Commerce.createComponentOverride<
    IProductComponent
>('Product', { component: ProductCard, ...PriceComponentActions });

export default ProductComponent;
